import React, { useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartForecasts, type ForecastRow } from './forecasting';

export default function ForecastResults({ rows }: { rows: ForecastRow[] }) {
  const seriesOptions = useMemo(() => [...new Set(rows.map(row => `${row.series_id} / ${row.target}`))], [rows]);
  const [series, setSeries] = useState('');
  const [origin, setOrigin] = useState('');
  const origins = useMemo(() => [...new Set(rows.filter(row => `${row.series_id} / ${row.target}` === series).map(row => row.forecast_origin))], [rows, series]);
  useEffect(() => { if (!seriesOptions.includes(series)) setSeries(seriesOptions[0] || ''); }, [seriesOptions, series]);
  useEffect(() => { if (!origins.includes(origin)) setOrigin(origins[0] || ''); }, [origins, origin]);
  const chart = useMemo(() => chartForecasts(rows, series, origin), [rows, series, origin]);
  return <Stack spacing={2}>
    <Typography variant="h6">Forecast and observed values</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextField select label="Series / target" value={series} onChange={event => setSeries(event.target.value)} fullWidth>
        {seriesOptions.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>
      <TextField select label="Forecast origin" value={origin} onChange={event => setOrigin(event.target.value)} fullWidth>
        {origins.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>
    </Stack>
    <Box height={360}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chart}>
          <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="horizon" /><YAxis domain={['auto', 'auto']} />
          <Tooltip labelFormatter={value => `Horizon ${value}`} />
          <Area dataKey="band" name="Forecast interval" stroke="none" fill="#90caf9" fillOpacity={0.3} />
          <Line dataKey="prediction" name="Median forecast" stroke="#1976d2" dot={false} />
          <Line dataKey="actual" name="Observed" stroke="#2e7d32" dot={false} connectNulls={false} />
          <Line dataKey="baseline" name="Last-value baseline" stroke="#999" strokeDasharray="4 4" dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
    <Typography variant="caption">The horizontal axis shows future observations after the selected origin. Shading shows the selected quantile interval.</Typography>
  </Stack>;
}
